import { useEffect, useMemo, useRef } from 'react';
import { useDebounce } from 'use-debounce';

import { useReposInfinite, useSelectedTenant } from '@/hooks/useSwr';

const PAGE_SIZE = 20;

export function usePromptNameDuplicateCheck(promptName: string) {
  const { data: selectedTenant } = useSelectedTenant();
  const [debouncedSearchTerm] = useDebounce(promptName, 500);

  const {
    data: promptsPages,
    isValidating: isValidatingPrompts,
    setSize,
  } = useReposInfinite(
    debouncedSearchTerm
      ? {
          query: debouncedSearchTerm,
          tenant_id: selectedTenant?.id,
        }
      : null
  );

  // Track the last checked page index to avoid re-checking
  const lastCheckedPageIndex = useRef(-1);

  // Reset the last checked index when the search term changes
  useEffect(() => {
    lastCheckedPageIndex.current = -1;
  }, [debouncedSearchTerm]);

  // Check only newly fetched pages for duplicate prompt names and load more if needed
  const hasDuplicate = useMemo(() => {
    if (!promptsPages || !debouncedSearchTerm) return false;

    const startIndex = lastCheckedPageIndex.current + 1;
    const endIndex = promptsPages.length;

    for (let i = startIndex; i < endIndex; i++) {
      const page = promptsPages[i];
      const duplicate = page.repos.find(
        (repo) => repo.repo_handle === debouncedSearchTerm
      );
      if (duplicate) {
        lastCheckedPageIndex.current = i;
        return true;
      }
    }

    lastCheckedPageIndex.current = endIndex - 1;

    if (promptsPages.length > 0) {
      const lastPage = promptsPages[promptsPages.length - 1];
      const totalItems = lastPage.total;
      const currentItems = promptsPages.length * PAGE_SIZE;

      if (currentItems < totalItems) {
        setSize(promptsPages.length + 1);
      }
    }

    return false;
  }, [promptsPages, debouncedSearchTerm, setSize]);

  return {
    hasDuplicate,
    isValidating: isValidatingPrompts,
  };
}
